'use client';

import { memo } from 'react';
import {
  Control,
  UseFormRegister,
  UseFormSetValue,
  useFormState,
  UseFormTrigger,
} from 'react-hook-form';
import NextButton from '@/components/Button/NextButton';
import Input from '@/components/Input/Input';
import { FormData } from '@/types/form';
import { fieldRules } from '@/utils/form';
import { FormDocumentData, Simplify } from '../../../../../prismicio-types';
import { WarningIcon } from '@/assets/icons/WarningIcon';
import TransparentLabel from '../TransparentLabel/TransparentLabel';
import useModal from '@/hooks/useModal';
import generalStyles from '../FormBody.module.scss';
import styles from './StepTwo.module.scss';

interface Props {
  data?: Simplify<FormDocumentData>;
  register?: UseFormRegister<FormData>;
  handleSubmit?: (e?: React.BaseSyntheticEvent) => Promise<void>;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control?: Control<FormData, any, FormData>;
  setValue?: UseFormSetValue<FormData>;
  trigger?: UseFormTrigger<FormData>;
  isModal?: boolean;
  verificationError?: boolean;
}

const quickSelectValues = [
  20000, 30000, 40000, 50000, 60000, 70000, 80000, 90000,
];

const BorrowSumForm = ({
  data,
  register,
  handleSubmit,
  control,
  setValue,
  trigger,
  isModal = false,
  verificationError = false,
}: Props) => {
  const { closeModal } = useModal();
  const { errors } = useFormState({ control });
  const handleQuickSelect = (value: number) => {
    const formattedValue = value.toLocaleString('en-US');
    setValue?.('borrow_sum', formattedValue);
    trigger?.('borrow_sum');
  };

  const submitForm = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const isValid = await trigger?.();
    if (isValid) {
      await handleSubmit?.();
      if (isModal) {
        closeModal();
      }
    }
  };

  const handleSumChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const rawValue = input.value.replace(/\D/g, '');
    const cursorPosition = input.selectionStart || 0;

    const parsedValue = parseInt(rawValue, 10);
    const formattedValue = Number.isNaN(parsedValue)
      ? ''
      : parsedValue.toLocaleString('en-US');

    const addedChars = formattedValue.length - input.value.length;

    e.target.value = formattedValue;
    setValue?.('borrow_sum', formattedValue);

    setTimeout(() => {
      const newCursorPosition = cursorPosition + addedChars;
      input.setSelectionRange(newCursorPosition, newCursorPosition);
    });
  };

  return (
    <form className={styles.form} onSubmit={submitForm}>
      {register && (
        <Input
          inputMode="numeric"
          type="text"
          id="borrow_sum"
          register={register('borrow_sum', {
            ...fieldRules.borrow_sum,
            onChange: handleSumChange,
          })}
          error={errors?.borrow_sum}
          isBorrowSumInput
        />
      )}
      <div className={styles.quickSelectWrapper}>
        <span className={generalStyles.quickSelectLabel}>Quick select</span>
        <div
          className={`${styles.selectButtonsWrapper} ${isModal ? styles.modalSelectButtonsWrapper : ''}`}
        >
          {quickSelectValues.map((value) => (
            <button
              onClick={() => handleQuickSelect(value)}
              type="button"
              key={value}
              className={generalStyles.selectionButton}
            >
              $ {value.toLocaleString('en-US')}
            </button>
          ))}
        </div>
      </div>
      <NextButton
        buttonType="submit"
        type="button"
        text={isModal ? 'Apply changes' : 'Next step'}
        styles={`${generalStyles.button} ${isModal ? generalStyles.smallButton : ''}`}
        isArrow={!isModal}
      />
      {verificationError && (
        <p
          className={`${generalStyles.error} ${generalStyles.verificationError}`}
        >
          <WarningIcon className={generalStyles.warningIcon} />
          <span>We couldn&apos;t verify that you are human</span>
        </p>
      )}
      {data && (
        <div className={styles.labelsWrapper}>
          {data.step2_labels.map((label) => (
            <TransparentLabel
              key={label.text}
              text={label.text}
              icon={label.icon}
            />
          ))}
        </div>
      )}
    </form>
  );
};

export default memo(BorrowSumForm);
